/**
 * Drop + Source Performance Tags API Hooks
 * Split from useSMSWiser.ts to keep file size within limits.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';

import api from '@/services/api';

import {
  dashboardParamsKey,
  smswiserKeys,
  unwrapApiData,
  useDropsViewPerfTagsEnabled,
  type ListPerformanceTagsParams,
  type PerformanceTag,
} from './useSMSWiser';

export interface DropPerformanceTagsResponse {
  tagsByDropId: Record<string, PerformanceTag[]>;
  tagDetailsByDropId?: Record<string, Array<{ tag: string; dateRangePreset: string }>>;
  /** Rules using source_* conditions — hidden outside the "no grouping" view. */
  sourceConditionRuleNames?: string[];
  /** Rules using list_* conditions — same display rules as source ones. */
  listConditionRuleNames?: string[];
  /** Rule name -> emoji ("+" / "-") for the drops-tab tag display toggle. */
  tagEmojis?: Record<string, string>;
  tagColors?: Record<string, string>;
  currency?: string;
  from: string;
  to: string;
}

export interface SourcePerformanceTagsResponse {
  tagsBySourceId: Record<string, PerformanceTag[]>;
  tagDetailsBySourceId?: Record<string, Array<{ tag: string; dateRangePreset: string }>>;
  tagColors?: Record<string, string>;
  currency?: string;
  from: string;
  to: string;
}

function buildPerfTagsParams(
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
): Record<string, string> {
  const params: Record<string, string> = {};
  if (wiserIds != null && wiserIds.length > 0) {
    params.wiserIds = wiserIds.join(',');
  }
  if (dashboardParams?.from) params.from = dashboardParams.from;
  if (dashboardParams?.to) params.to = dashboardParams.to;
  if (dashboardParams?.tz) params.tz = dashboardParams.tz;
  if (dashboardParams?.currency) params.currency = dashboardParams.currency;
  return params;
}

export function useDropPerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
  enabled = true,
) {
  const queryEnabled = useDropsViewPerfTagsEnabled(countryKey, enabled);
  return useQuery({
    queryKey: [
      ...smswiserKeys.country(countryKey),
      'drop-performance-tags',
      wiserIds ?? [],
      dashboardParamsKey(dashboardParams),
    ],
    queryFn: async () => {
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/drop-performance-tags`,
        { params: buildPerfTagsParams(wiserIds, dashboardParams) },
      );
      return unwrapApiData<DropPerformanceTagsResponse>(response.data);
    },
    enabled: queryEnabled,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
}

export function useSourcePerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
  enabled = true,
) {
  const queryEnabled = useDropsViewPerfTagsEnabled(countryKey, enabled);
  return useQuery({
    queryKey: [
      ...smswiserKeys.country(countryKey),
      'source-performance-tags',
      wiserIds ?? [],
      dashboardParamsKey(dashboardParams),
    ],
    queryFn: async () => {
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/source-performance-tags`,
        { params: buildPerfTagsParams(wiserIds, dashboardParams) },
      );
      return unwrapApiData<SourcePerformanceTagsResponse>(response.data);
    },
    enabled: queryEnabled,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
}
